export type SettingsPath =
  | "/settings/account"
  | "/settings/privacy"
  | "/settings/general"
  | "/settings/world"
  | "/settings/appearance"
  | "/settings/keybindings"
  | "/settings/subscription-accounts"
  | "/settings/connectors"
  | "/settings/provider-keys"
  | "/settings/neucleos-access"
  | "/settings/local-models"
  | "/settings/tools"
  | "/settings/instructions"
  | "/settings/connections"
  | "/settings/permissions"
  | "/settings/archived";

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly to: SettingsPath;
  readonly targetId?: string;
  /**
   * Search-only words for the setting (#8831 as reference): what the row's
   * description says, in the person's words, so "zoom" finds the row titled
   * "Density" and "notification sound" finds the sound picker. Never rendered
   * as the title; shown as the result's subtitle when it is what matched.
   */
  readonly detail?: string;
}

/**
 * Section labels in sidebar order. The sidebar nav and the search-result
 * subtitles both render from this record, so each label exists once.
 */
export const SETTINGS_SECTION_LABELS: Readonly<Record<SettingsPath, string>> = {
  "/settings/account": "Account",
  "/settings/privacy": "Privacy",
  "/settings/general": "General",
  "/settings/world": "World",
  "/settings/appearance": "Appearance",
  "/settings/keybindings": "Keybindings",
  "/settings/subscription-accounts": "Subscription accounts",
  "/settings/connectors": "Connectors",
  "/settings/provider-keys": "Provider keys",
  "/settings/neucleos-access": "Neucleos access",
  "/settings/local-models": "Local models",
  "/settings/tools": "Tools",
  "/settings/instructions": "Agent Instructions",
  "/settings/connections": "Connections",
  "/settings/permissions": "Permissions",
  "/settings/archived": "Archive",
};

/** A known settings section that the nav, search, and restore may address. */
export function isNavigableSettingsPath(value: unknown): value is SettingsPath {
  return typeof value === "string" && Object.hasOwn(SETTINGS_SECTION_LABELS, value);
}

/**
 * Every searchable setting, in result order. This catalog is the single
 * source of truth for anchor ids and visible titles: panels render both via
 * `searchableSetting`, so a retitle (or, later, a translation pass) happens
 * here once instead of separately in the panel and the index.
 */
export const SETTINGS_SEARCH_ITEMS = [
  {
    id: "cleo-computer",
    title: "Local Mac",
    to: "/settings/general",
    detail: "this Mac as a local environment, its name and folders",
  },
  {
    id: "light-study",
    title: "Light study",
    to: "/settings/appearance",
    detail: "the light source that paints panels, glow direction",
  },
  {
    id: "light-intensity",
    title: "Light intensity",
    to: "/settings/appearance",
    detail: "how bright the panel light is",
  },
  {
    id: "light-breathe",
    title: "Breathe",
    to: "/settings/appearance",
    detail: "the light breathes slowly, ambient pulse",
  },
  {
    id: "hover-light",
    title: "Hover light",
    to: "/settings/appearance",
    detail: "a light follows the pointer over doors",
  },
  {
    id: "opaque-doors",
    title: "Opaque doors",
    to: "/settings/appearance",
    detail: "doors lose their glass and turn solid",
  },
  {
    id: "door-fade",
    title: "Door fade",
    to: "/settings/appearance",
    detail: "how fast doors fade in and out",
  },
  {
    id: "card-glow",
    title: "Card glow",
    to: "/settings/appearance",
    detail: "cards glow at their edge",
  },

  {
    id: "account-profile",
    title: "Profile",
    to: "/settings/account",
    detail: "your name, photo and contact details",
  },
  {
    id: "account-photo-row",
    title: "Profile photo",
    to: "/settings/account",
  },
  {
    id: "account-session",
    title: "Account",
    to: "/settings/account",
    detail: "signed in identity, sign out",
  },
  {
    id: "account-session-row",
    title: "Sign in / sign out",
    to: "/settings/account",
  },
  {
    id: "account-world-access",
    title: "World access",
    to: "/settings/account",
    detail: "which Worlds you can enter, invitations",
  },
  {
    id: "account-name",
    title: "Name",
    to: "/settings/account",
  },
  {
    id: "account-contact",
    title: "Contact",
    to: "/settings/account",
  },
  {
    id: "voice-vocabulary",
    title: "Speech vocabulary",
    to: "/settings/account",
    detail: "words dictation should recognize, custom speech terms",
  },
  {
    id: "account-worlds",
    title: "Your Worlds",
    to: "/settings/account",
  },
  {
    id: "account-cleo-connect",
    title: "Remote access sign-in",
    to: "/settings/account",
    detail: "sign a headless or SSH environment in to Cleo Connect, device code",
  },
  {
    id: "privacy",
    title: "Privacy",
    to: "/settings/privacy",
  },
  {
    // CM32 (dated revision 2026-08-30): the row's one seat moved from the
    // Account page into Settings → Privacy — same verb, route, and fold.
    id: "presence-visibility",
    title: "Presence",
    to: "/settings/privacy",
    // Rendered only when signed in with an active World, so the
    // always-present section is the stable scroll destination.
    targetId: "privacy",
    detail: "who sees you online, presence indicator",
  },
  {
    id: "privacy-receipts",
    title: "Read receipts",
    to: "/settings/privacy",
    // Rendered only when signed in with an active World, like Presence.
    targetId: "privacy",
    detail: "read receipts other members see",
  },
  {
    id: "privacy-leaving",
    title: "Leaving this Mac",
    to: "/settings/privacy",
    detail: "erase this Mac's local data when you leave, wipe, forget",
  },
  {
    id: "privacy-analytics",
    title: "Analytics",
    to: "/settings/privacy",
    detail: "usage analytics, telemetry",
  },
  {
    id: "privacy-trace-export",
    title: "Trace export",
    to: "/settings/privacy",
    detail: "export traces for debugging, observability",
  },
  {
    id: "privacy-diagnostics",
    title: "Local diagnostics",
    to: "/settings/privacy",
    detail: "local diagnostics files, logs kept on this Mac",
  },
  {
    id: "color-scheme",
    title: "Color scheme",
    to: "/settings/appearance",
    // The scheme tiles sit at the top of the Appearance section.
    targetId: "appearance",
    detail: "light, dark or system, dark mode",
  },
  {
    id: "theme",
    title: "Themes",
    to: "/settings/appearance",
    // Theme cards live directly under the scheme tiles; the section is the
    // stable scroll destination for both.
    targetId: "appearance",
    detail: "theme editor, colors, VS Code themes, Open VSX",
  },
  {
    id: "density",
    title: "Density",
    to: "/settings/appearance",
    detail: "compact or comfortable spacing, interface zoom, larger or smaller",
  },
  {
    // HIG3 (GOALS/GOALS-DESIGN.html#hig3): the person's contrast choice.
    id: "contrast",
    title: "Contrast",
    to: "/settings/appearance",
    detail:
      "raise text and border contrast, high contrast, opaque panels, system increase contrast",
  },
  {
    // HIG4: the one effects switch in front; the knobs sit behind Advanced.
    id: "reduce-effects",
    title: "Reduce effects",
    to: "/settings/appearance",
    detail: "fewer animations and blur, reduce motion",
  },
  {
    // Prefixed because the slider control already owns the `glass-opacity` id.
    id: "setting-glass-opacity",
    title: "Glass opacity",
    to: "/settings/appearance",
    detail: "how see-through the glass panels are, transparency",
  },
  {
    id: "environment-identification",
    title: "Environment identification",
    to: "/settings/appearance",
    // The setting is stage-dependent, so its parent section is the stable destination.
    targetId: "appearance",
    detail: "show which environment a thread runs in, badge, color",
  },
  {
    id: "interface-font",
    title: "Interface font",
    to: "/settings/appearance",
    detail: "the font the interface uses, typeface",
  },
  {
    id: "prompt-font",
    title: "Prompt font",
    to: "/settings/appearance",
    detail: "the font of the composer prompt",
  },
  {
    id: "code-font",
    title: "Code font",
    to: "/settings/appearance",
    detail: "monospace font for code blocks and diffs",
  },
  {
    id: "terminal-font",
    title: "Terminal font",
    to: "/settings/appearance",
    detail: "the terminal's font",
  },
  {
    id: "font-smoothing",
    title: "Font smoothing",
    to: "/settings/appearance",
    detail: "subpixel or grayscale antialiasing",
  },
  {
    id: "word-wrap",
    title: "Word wrap",
    to: "/settings/appearance",
    detail: "wrap long lines in code and diffs",
  },
  {
    id: "tone-down-sections",
    title: "Tone down colors",
    to: "/settings/appearance",
    detail: "mute section accent colors",
  },
  {
    id: "ambient-motion",
    title: "Ambient motion",
    to: "/settings/appearance",
    detail: "background motion, moving light",
  },
  {
    id: "working-indicator",
    title: "Working indicator",
    to: "/settings/appearance",
    detail: "how a working session shows progress, spinner, orb",
  },
  {
    id: "working-indicator-orb-loop",
    title: "Orb loop",
    to: "/settings/appearance",
  },
  {
    id: "working-indicator-loader",
    title: "Loader",
    to: "/settings/appearance",
  },
  {
    id: "shell-glass",
    title: "Frosted panels",
    to: "/settings/appearance",
    detail: "frosted glass panels, blur",
  },
  {
    id: "project-grouping",
    title: "Project grouping",
    to: "/settings/general",
    detail: "group projects by folder or repository in the sidebar",
  },
  {
    id: "auto-settle-inactive-threads",
    title: "Auto-settle inactive threads",
    to: "/settings/general",
    detail: "archive threads that have been idle, cleanup",
  },
  {
    id: "time-format",
    title: "Time format",
    to: "/settings/general",
    detail: "12-hour or 24-hour clock",
  },
  {
    id: "provider-update-checks",
    title: "Provider update checks",
    to: "/settings/general",
    detail: "check for engine updates, Claude Code, Codex CLI versions",
  },
  {
    id: "add-project-starts-in",
    title: "Add project starts in",
    to: "/settings/general",
    detail: "the folder the add project picker opens in, base directory",
  },
  {
    id: "queue-messages",
    title: "Queue messages while Cleo works",
    to: "/settings/general",
    detail: "queueing, unread, steer, stop and send, send while she is working",
  },
  {
    id: "archive-confirmation",
    title: "Archive confirmation",
    to: "/settings/general",
    detail: "ask before archiving a thread",
  },
  {
    id: "delete-confirmation",
    title: "Delete confirmation",
    to: "/settings/general",
    detail: "ask before deleting",
  },
  {
    id: "diagnostics",
    title: "Diagnostics",
    to: "/settings/general",
    detail: "server logs, version, connection health, debug",
  },
  {
    id: "legacy-plan-mode",
    title: "Plan mode (legacy)",
    to: "/settings/general",
    detail: "the old plan mode toggle",
  },
  {
    id: "world-name",
    title: "World name",
    to: "/settings/world",
    detail: "rename this World",
  },
  {
    id: "world-mark",
    title: "World mark",
    to: "/settings/world",
    detail: "the World's emblem, icon, logo",
  },
  {
    id: "world-members",
    title: "Members",
    to: "/settings/world",
    detail: "see members and founder requests; invite, remove and restore are not available yet",
  },
  {
    id: "world-members-roles",
    title: "Members and roles",
    to: "/settings/world",
    detail: "founder role requests; inviting, removing and restoring members are not available yet",
  },
  {
    id: "world-usage",
    title: "Usage",
    to: "/settings/world",
    detail: "tokens and cost this World used",
  },
  {
    id: "world-usage-ledger",
    title: "World usage",
    to: "/settings/world",
  },
  {
    id: "world-selling",
    title: "Selling",
    to: "/settings/world",
    detail:
      "World merchant Stripe account, business contact, onboarding, payment and payout readiness",
  },
  {
    id: "world-billing",
    title: "Billing",
    to: "/settings/world",
    detail: "subscription, invoices, payment method, Stripe portal",
  },
  {
    id: "world-billing-portal",
    title: "Billing Portal",
    to: "/settings/world",
  },
  {
    id: "world-accent",
    title: "World accent",
    to: "/settings/world",
    detail: "the accent color every member's surfaces default to",
  },
  {
    id: "keybindings",
    title: "Keybindings",
    to: "/settings/keybindings",
    detail: "keyboard shortcuts, hotkeys, key bindings, rebind a command, when expression",
  },
  {
    id: "background-activity",
    title: "Background activity",
    to: "/settings/general",
    detail: "how much work runs while the app is in the background, energy",
  },
  {
    id: "provider-health-check-interval",
    title: "Provider health check interval",
    to: "/settings/general",
    detail: "how often engines are probed, health poll",
  },
  {
    id: "subscription-accounts",
    title: "Subscription accounts",
    to: "/settings/subscription-accounts",
    detail: "Claude and Codex sign-ins, subscriptions",
  },
  {
    id: "connectors",
    title: "Connectors",
    to: "/settings/connectors",
    detail: "ChatGPT and other outside clients that act for you in a World, MCP",
  },
  {
    id: "provider-keys",
    title: "Provider keys",
    to: "/settings/provider-keys",
    detail: "API keys for model providers, OpenRouter, Anthropic key, OpenAI key",
  },
  {
    id: "neucleos-access",
    title: "Neucleos access",
    to: "/settings/neucleos-access",
    detail: "Neucleos-hosted models and access",
  },
  {
    id: "local-models",
    title: "Local models",
    to: "/settings/local-models",
    detail: "Ollama and LM Studio models running on this Mac",
  },
  {
    id: "session-injection",
    title: "Agent instructions",
    to: "/settings/instructions",
    detail: "system prompt, instructions every agent receives, CLAUDE.md, AGENTS.md",
  },
  {
    id: "session-injection-all",
    title: "This World’s prompts for all agents",
    to: "/settings/instructions",
  },
  {
    id: "session-injection-cleo",
    title: "Cleo instructions",
    to: "/settings/instructions",
  },
  {
    id: "session-injection-hand",
    title: "Hand instructions",
    to: "/settings/instructions",
  },
  {
    id: "session-injection-worlds",
    title: "Your prompts in this World",
    to: "/settings/instructions",
  },
  {
    id: "session-injection-founder",
    title: "Founder prompts in this World",
    to: "/settings/instructions",
  },
  {
    id: "music-provider",
    title: "Music service",
    to: "/settings/connections",
    detail: "Spotify or Apple Music playback",
  },
  {
    id: "spotify-client-id",
    title: "Spotify application",
    to: "/settings/connections",
  },
  {
    id: "remote-environments",
    title: "Remote environments",
    to: "/settings/connections",
    detail: "SSH environments, remote machines, tunnels",
  },
  {
    id: "neucleos-world",
    title: "Neucleos",
    to: "/settings/connections",
    detail: "the Neucleos World connection, sign in to a World",
  },
  {
    id: "default-runtime-mode",
    title: "Default permission mode for new sessions",
    to: "/settings/general",
    detail:
      "Supervised, Auto-accept edits, Auto or Full access for a new session, approval, permissions",
  },
  {
    id: "response-streaming-mode",
    title: "Reply painting while a reply streams",
    to: "/settings/general",
    detail: "whole reply, finished paragraphs or every token, streaming",
  },
  {
    id: "default-session-model",
    title: "Default model for new rooms",
    to: "/settings/general",
    detail: "the model a new room's sessions start on",
  },
  {
    id: "scoped-setting-overrides",
    title: "World and room overrides",
    to: "/settings/general",
    detail: "World default and room override, reset restores inheritance, per-room settings",
  },
  {
    id: "session-notifications",
    title: "Session notifications",
    to: "/settings/general",
    detail:
      "system notification when a background session completes, fails or needs an answer, opt-in",
  },
  {
    id: "session-notifications-in-app",
    title: "In-app session notices",
    to: "/settings/general",
    detail: "toast notice with Open session while the Desk is focused",
  },
  {
    id: "session-notification-badge",
    title: "Session notification badge",
    to: "/settings/general",
    detail: "dock badge, numbered favicon, unread count",
  },
  {
    id: "session-notification-sound",
    title: "Notification sound",
    to: "/settings/general",
    detail: "chime on completion or attention, sound picker, mute",
  },
  {
    id: "default-session-tools",
    title: "Default tools for new sessions",
    to: "/settings/tools",
    detail: "tools a new session starts with, nothing by default, tool boundary",
  },
  {
    id: "text-generation-model",
    title: "Background text model",
    to: "/settings/general",
    detail: "the model used for titles, summaries and other background text",
  },
  {
    id: "world-folders",
    title: "World folders",
    to: "/settings/world",
    detail:
      "the World's folders and code folders on this Mac that Cleo can search, codebase, files folders, source folders",
  },
  {
    id: "browser-profiles",
    title: "Browser profiles",
    to: "/settings/general",
    detail: "separate browser profiles and partitions for the in-app browser, cookies",
  },
  {
    id: "desktop-window-capture",
    title: "Window capture",
    to: "/settings/general",
    detail:
      "capture any window into the composer with its app name, title and text, Screen Recording, Accessibility, permissions asked only when turned on",
  },
  {
    id: "desktop-window-capture-shortcut",
    title: "Capture shortcut",
    to: "/settings/general",
    detail:
      "the key chord that captures a window, conflict-checked against Cleo, the system and other apps",
  },
  {
    id: "local-environment",
    title: "Local environment",
    to: "/settings/general",
    detail: "run sessions on this Mac, local environment on or off",
  },
  {
    id: "mac-permissions",
    title: "Permissions",
    to: "/settings/permissions",
    detail: "macOS permissions the Desk asks for, System Settings",
  },
  {
    id: "full-disk-access",
    title: "Full Disk Access",
    to: "/settings/permissions",
    // Individual permission rows render from the live probe, so the section
    // is the stable scroll destination.
    targetId: "mac-permissions",
    detail: "read files anywhere on this Mac",
  },
  {
    id: "accessibility",
    title: "Accessibility",
    to: "/settings/permissions",
    targetId: "mac-permissions",
    detail: "control other apps, automation",
  },
  {
    id: "screen-recording",
    title: "Screen Recording",
    to: "/settings/permissions",
    targetId: "mac-permissions",
    detail: "capture the screen or a window",
  },
  {
    id: "microphone",
    title: "Microphone",
    to: "/settings/permissions",
    targetId: "mac-permissions",
    detail: "dictation and voice input",
  },
  {
    id: "open-source-notices",
    title: "Open-source notices",
    to: "/settings/general",
    detail: "third-party licenses, MIT, Apache, CC0, attribution, the packages Cleo ships",
  },
  {
    id: "archive",
    title: "Archived threads",
    to: "/settings/archived",
    detail: "archived and settled threads, restore",
  },
] as const satisfies ReadonlyArray<SettingsSearchItem>;

export type SettingsSearchItemId = (typeof SETTINGS_SEARCH_ITEMS)[number]["id"];

const SEARCH_ITEMS_BY_ID = Object.fromEntries(
  SETTINGS_SEARCH_ITEMS.map((item) => [item.id, item]),
) as Readonly<Record<SettingsSearchItemId, SettingsSearchItem>>;

/**
 * `id` and `title` props for the element a search item anchors to. Panels
 * spread (or pick from) this instead of restating the strings, so the catalog
 * and the rendered settings cannot drift apart.
 */
export function searchableSetting(id: SettingsSearchItemId): {
  readonly id: string;
  readonly title: string;
} {
  const { id: anchorId, title } = SEARCH_ITEMS_BY_ID[id];
  return { id: anchorId, title };
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** What a result matched on, strongest first: its title, its section's name, or its detail words. */
export type SettingsSearchMatch = "title" | "section" | "detail";

const MATCH_RANK: Readonly<Record<SettingsSearchMatch, number>> = {
  title: 0,
  section: 1,
  detail: 2,
};

/**
 * How one item matches a query (#8831 as reference), token-aware: every
 * word of the query must appear somewhere, and the strongest field that
 * covers the whole query names the match. A title that contains the query
 * as one phrase ranks above a title that only holds its words scattered.
 */
export function settingsSearchMatch(
  query: string,
  item: SettingsSearchItem,
): SettingsSearchMatch | null {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return null;
  const words = normalizedQuery.split(" ");
  const title = normalizeSearchText(item.title);
  const section = normalizeSearchText(SETTINGS_SECTION_LABELS[item.to]);
  const detail = normalizeSearchText(item.detail ?? "");
  if (words.every((word) => title.includes(word))) return "title";
  if (words.every((word) => title.includes(word) || section.includes(word))) return "section";
  if (
    detail.length > 0 &&
    words.every((word) => title.includes(word) || section.includes(word) || detail.includes(word))
  ) {
    return "detail";
  }
  return null;
}

export function searchSettings(
  query: string,
  items: ReadonlyArray<SettingsSearchItem> = SETTINGS_SEARCH_ITEMS,
): ReadonlyArray<SettingsSearchItem> {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  const ranked: Array<{
    readonly item: SettingsSearchItem;
    readonly rank: number;
    readonly index: number;
  }> = [];
  items.forEach((item, index) => {
    const match = settingsSearchMatch(normalizedQuery, item);
    if (match === null) return;
    const title = normalizeSearchText(item.title);
    // Phrase in the title beats scattered words in the title; a title that
    // STARTS with the phrase beats one that merely contains it.
    const phrase =
      match === "title" && title.startsWith(normalizedQuery)
        ? 0
        : match === "title" && title.includes(normalizedQuery)
          ? 1
          : 2;
    ranked.push({ item, rank: MATCH_RANK[match] * 3 + phrase, index });
  });
  // Stable within a rank: the catalog's own order decides ties.
  ranked.sort((a, b) => a.rank - b.rank || a.index - b.index);
  return ranked.map((entry) => entry.item);
}

/**
 * The result's second line: the section's name, or the detail words when
 * they are what matched — so the person sees WHY "zoom" found "Density".
 */
export function settingsSearchResultSubtitle(query: string, item: SettingsSearchItem): string {
  return settingsSearchMatch(query, item) === "detail" && item.detail !== undefined
    ? item.detail
    : SETTINGS_SECTION_LABELS[item.to];
}
